import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContentTag } from "@/components/ui/content-tag";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import useWordpressCategories from "@/hooks/useWordpressCategories";
import { Category } from "@/types/settings.types";

export default function WordPressCategoriesCard() {
  const { currentWebsite } = useWebsites();
  const { settings: wpSettings } = useWordPress();
  const { categories, setCategories } = useSettingsState();
  const { 
    fetchCategories,
    fetchCategoriesFromWordPress, 
    generateCategoryIdeas, 
    addCategory, 
    handleDeleteCategory,
    handleRefreshCategories,
    pushCategoriesToWordPress
  } = useWordpressCategories();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [generatedCategories, setGeneratedCategories] = useState<any[]>([]);

  // Fetch categories when component mounts or website changes
  useEffect(() => {
    if (currentWebsite && wpSettings?.is_connected) {
      // Use fetchCategories instead of handleRefreshCategories to avoid generating new suggestions
      fetchCategories();
    }
  }, [currentWebsite, wpSettings?.is_connected]);

  const handleFetchCategories = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }
    
    setIsLoading(true);
    try {
      await fetchCategoriesFromWordPress();
      toast.success("Categories refreshed from WordPress");
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories from WordPress");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateSuggestions = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }
    
    setIsGenerating(true);
    try {
      await generateCategoryIdeas();
      // The generated categories will be set in the context
      toast.success("Category suggestions generated");
    } catch (error) {
      console.error("Error generating category suggestions:", error);
      toast.error("Failed to generate category suggestions");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddCategory = async (category: any) => {
    try {
      await addCategory(category);
      toast.success(`Category "${category.name}" added successfully`);
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error("Failed to add category");
    }
  };

  const handleRemoveCategory = async (category: Category) => {
    try {
      await handleDeleteCategory(category);
      toast.success(`Category "${category.name}" removed successfully`);
    } catch (error) {
      console.error("Error removing category:", error);
      toast.error("Failed to remove category");
    }
  };

  const handlePushToWordPress = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }
    
    setIsPushing(true);
    try {
      await pushCategoriesToWordPress();
      toast.success("Categories pushed to WordPress successfully");
    } catch (error) {
      console.error("Error pushing categories to WordPress:", error);
      toast.error("Failed to push categories to WordPress");
    } finally {
      setIsPushing(false);
    }
  };

  // Don't show the card if WordPress is not connected
  if (!wpSettings?.is_connected) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          WordPress Categories
        </CardTitle>
        <CardDescription>
          Manage your WordPress categories and get AI-powered suggestions for new ones.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleFetchCategories}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh from WordPress
          </Button>
          <Button 
            variant="outline" 
            onClick={handleGenerateSuggestions}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Generate Suggestions
          </Button>
          <Button 
            variant="default" 
            onClick={handlePushToWordPress}
            disabled={isPushing || categories.length === 0}
          >
            {isPushing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Push to WordPress
          </Button>
        </div>

        {categories.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Add some categories first, then use "Push to WordPress" to sync them with your WordPress site.
          </p>
        )}

        {/* Existing Categories */}
        <div className="space-y-3">
          <Label>Your Categories ({categories.length})</Label>
          {categories.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <div key={category.wp_category_id} className="relative group">
                  <ContentTag
                    type="category"
                    label={category.name}
                  />
                  <button
                    onClick={() => handleRemoveCategory(category)}
                    className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title="Remove category"
                  >
                    <Trash2 className="h-2 w-2" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No categories found. Click "Refresh from WordPress" to fetch your existing categories.
            </p>
          )}
        </div>

        {/* Generated Category Suggestions */}
        {generatedCategories.length > 0 && (
          <div className="space-y-3">
            <Label>Suggested Categories</Label>
            <div className="flex flex-wrap gap-2">
              {generatedCategories.map((category) => (
                <Button 
                  key={category.slug} 
                  variant="secondary" 
                  size="sm"
                  onClick={() => handleAddCategory(category)}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Categories help organize your content on WordPress and improve navigation. 
          You can fetch existing categories from your WordPress site or generate AI-powered suggestions.
        </div>
      </CardContent>
    </Card>
  );
}
